import { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVerticalIcon, TableIcon, Trash2Icon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";
import { Input } from "@/components/ui/input";
import type { BaseTemplateAttribute, Selection } from "./types";
import { TabularSchemaPreview } from "./tabular-schema-preview";
import { useEditorConfig } from "./editor-config-context";

interface TabularBlockProps {
  attribute: BaseTemplateAttribute;
  selection: Selection;
  isLocked: boolean;
  onSelectAttribute: (id: number) => void;
  onSelectColumn: (attributeId: number, columnSortOrder: number) => void;
  onUpdateName: (name: string) => void;
  onRemove: (id: number) => void;
  hasBlockGap: boolean;
}

export function TabularBlock({
  attribute,
  selection,
  isLocked: editorLocked,
  onSelectAttribute,
  onSelectColumn,
  onUpdateName,
  onRemove,
  hasBlockGap,
}: TabularBlockProps) {
  const { renderTabularBadge, isGeneratedAttribute } = useEditorConfig();
  const isLocked = editorLocked || (isGeneratedAttribute?.(attribute) ?? false);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(attribute.name || "");

  const commitName = () => {
    setIsEditing(false);
    if (draft !== (attribute.name || "")) {
      onUpdateName(draft);
    }
  };

  const startEditing = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isLocked) {
      setDraft(attribute.name || "");
      setIsEditing(true);
    }
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: attribute.id.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  } as React.CSSProperties;

  const isHeaderSelected =
    selection?.type === "attribute" && selection.attributeId === attribute.id;

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-attribute-id={attribute.id}
      className={
        "border border-border bg-background" + (hasBlockGap ? " mt-3" : "")
      }
    >
      {/* Header */}
      <div
        className={
          "group/header flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-muted/50 transition-colors " +
          (isHeaderSelected
            ? "border-l-2 border-l-[var(--farmless-green)] bg-olive-2"
            : "border-l-2 border-l-transparent")
        }
        onClick={() => onSelectAttribute(attribute.id)}
      >
        {/* Drag handle */}
        {!isLocked && (
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab p-0.5 text-muted-foreground hover:text-foreground opacity-0 group-hover/header:opacity-100 transition-opacity shrink-0"
            aria-label="Drag to reorder"
          >
            <GripVerticalIcon className="h-3 w-3" />
          </button>
        )}

        <TableIcon className="h-4 w-4 text-muted-foreground shrink-0" />

        {/* Name */}
        <div className="flex-1 min-w-0" onDoubleClick={startEditing}>
          {isEditing ? (
            <Input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commitName}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitName();
                if (e.key === "Escape") {
                  setDraft(attribute.name || "");
                  setIsEditing(false);
                }
              }}
              onClick={(e) => e.stopPropagation()}
              className="h-7 text-base font-medium px-1 -mx-1"
            />
          ) : (
            <span className="text-base font-medium truncate block">
              {attribute.name || (
                <span className="text-muted-foreground italic">
                  Untitled table
                </span>
              )}
            </span>
          )}
        </div>

        {/* Badges + Delete */}
        <div className="flex items-center gap-1 shrink-0">
          {renderTabularBadge(attribute)}
          {!isLocked && (
            <DeleteConfirmDialog
              title="Delete Table"
              description={`Are you sure you want to delete "${attribute.name || "Untitled"}"? This cannot be undone.`}
              onConfirm={() => onRemove(attribute.id)}
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0 text-muted-foreground hover:text-destructive opacity-0 group-hover/header:opacity-100 transition-opacity"
                >
                  <Trash2Icon className="h-3 w-3" />
                </Button>
              }
            />
          )}
        </div>
      </div>

      {/* Schema preview */}
      <TabularSchemaPreview
        templateAttributeId={attribute.id}
        selection={selection}
        isLocked={isLocked}
        onSelectColumn={onSelectColumn}
      />
    </div>
  );
}
